import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { cn, getFontFamily } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import FacebookIcon from '@/components/icons/FacebookIcon';
import InstagramIcon from '@/components/icons/InstagramIcon';
import YoutubeIcon from '@/components/icons/YoutubeIcon';
import { useTranslation } from 'react-i18next';

const accordionData = [
  { id: 1, titleKey: 'visualIdentity.endLivePreview.questions.quality' },
  { id: 2, titleKey: 'visualIdentity.endLivePreview.questions.experience' },
  { id: 3, titleKey: 'visualIdentity.endLivePreview.questions.comments' },
];

export default function PreviewEndLive({
  visualIdentity,
  accentColor,
  backgroundColor,
  theme,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  accentColor: string;
  backgroundColor: string;
  theme?: 'LIGHT' | 'DARK';
}) {
  const { t } = useTranslation('webinars');
  const currentTheme = theme || visualIdentity?.theme || 'LIGHT';
  return (
    <div
      className=" overflow-hidden w-full rounded-[20px] relative flex gap-4 items-start justify-between p-4"
      style={{
        backgroundColor: backgroundColor,
      }}
    >
      {/* left */}
      <div className="space-y-4 w-1/2">
        {visualIdentity?.logoUrl && (
          <div className="">
            <img src={visualIdentity?.logoUrl} alt="logo" className="w-12" />
          </div>
        )}
        <div>
          <p
            className={cn(
              currentTheme === 'DARK' ? 'text-secondary-inverse' : 'text-secondary',
              'text-secondary font-normal text-[10px]',
            )}
            style={{
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('thankyouPage.defaultSubtitle')}
          </p>
          <p
            className={cn(currentTheme === 'DARK' ? 'text-white' : 'text-black', 'text-[14px]')}
            style={{
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('thankyouPage.defaultTitle')}
          </p>
        </div>

        <p
          className={cn(
            currentTheme === 'DARK' ? 'text-secondary-inverse' : 'text-secondary',
            'text-[10px] whitespace-pre-line',
          )}
          style={{
            fontFamily: getFontFamily(visualIdentity?.fontFamily),
          }}
        >
          {t('thankyouPage.defaultEvaluationIntro')}
        </p>

        {/* Preview statique du formulaire d'évaluation */}
        <div className="w-full">
          <div className="flex flex-col gap-4">
            {accordionData.map((item) => (
              <div
                key={item.id}
                className="bg-[#FBFBFB] rounded-[20px] px-4 py-2 flex items-center justify-between "
                style={{
                  backgroundColor:
                    currentTheme === 'LIGHT'
                      ? 'rgba(31, 31, 31, 0.03)'
                      : currentTheme === 'DARK'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : undefined,
                }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-[10px]"
                    style={{
                      fontFamily: getFontFamily(visualIdentity?.fontFamily),
                      color: accentColor,
                    }}
                  >
                    {item.id}.
                  </span>{' '}
                  <span
                    className="text-[10px]"
                    style={{
                      fontFamily: getFontFamily(visualIdentity?.fontFamily),
                      color: currentTheme === 'DARK' ? 'white' : 'black',
                    }}
                  >
                    {t(item.titleKey)}
                  </span>
                </div>
                <ChevronDown
                  className="size-3 shrink-0 transition-transform duration-200 text-highlighted"
                  style={{ color: accentColor }}
                />
              </div>
            ))}
          </div>
        </div>

        <Button
          className="text-[10px] w-1/2 h-6"
          style={{
            backgroundColor: accentColor,
            fontFamily: getFontFamily(visualIdentity?.fontFamily),
          }}
        >
          {t('thankyouPage.evaluation.submit')}
        </Button>
      </div>
      {/* right */}
      <div className="w-1/2 flex flex-col gap-4">
        <div
          className="flex items-center justify-center flex-col gap-2 rounded-[20px] p-2"
          style={{
            backgroundColor:
              currentTheme === 'LIGHT'
                ? 'rgba(31, 31, 31, 0.03)'
                : currentTheme === 'DARK'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : undefined,
          }}
        >
          <p
            className={cn(
              currentTheme === 'DARK' ? 'text-white' : 'text-black',
              'text-[14px] text-center',
            )}
            style={{
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('visualIdentity.endLivePreview.offerTitle')}
          </p>
          <p
            className={cn(
              currentTheme === 'DARK' ? 'text-white' : 'text-black',
              'text-[6px] text-center',
            )}
            style={{
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('visualIdentity.endLivePreview.offerSubtitle')}
          </p>
          <Button
            className={cn('text-[10px] w-1/2 h-6')}
            style={{
              backgroundColor: accentColor,
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('thankyouPage.defaultBuyButton')}
          </Button>
        </div>

        <div>
          <img src="/assets/waiting-room.png" alt="image" className="w-full" />
        </div>
        <div
          className="flex items-center gap-2 rounded-[20px] p-2"
          style={{
            backgroundColor:
              currentTheme === 'LIGHT'
                ? 'rgba(31, 31, 31, 0.03)'
                : currentTheme === 'DARK'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : undefined,
          }}
        >
          <div className="flex gap-2">
            <img src="/assets/avatar.svg" alt="avatar" className="w-4 h-4" />
            <div>
              <p
                className={cn(currentTheme === 'DARK' ? 'text-white' : 'text-black', 'text-[10px]')}
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                }}
              >
                John Doe
              </p>
            </div>
          </div>

          <Separator className="w-full" orientation="vertical" />

          <div className="flex flex-col gap-2">
            <p
              className={cn(currentTheme === 'DARK' ? 'text-white' : 'text-black', 'text-[10px]')}
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              {t('thankyouPage.findMeOnSocial')}
            </p>
            <div className="flex items-center  gap-2">
              <FacebookIcon size={16} />
              <InstagramIcon size={16} />
              <YoutubeIcon size={16} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
